import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { TimeSeries } from "@aurumiq/ui/composite/charts";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { KpiTile } from "@aurumiq/ui/composite/kpi-tile";
import { dmy, grams, int, money, relativeTime } from "@aurumiq/ui/lib/format";
import { QueryState } from "@/components/query-state";
import type { DeviceMetrics, OrgMetrics } from "@/lib/types";
import { useOrgMetrics } from "./queries";

const col = createColumnHelper<DeviceMetrics>();

function Totals({ totals }: { totals: Record<string, number> }) {
  const entries = Object.entries(totals ?? {});
  if (!entries.length) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="grid gap-0.5">
      {entries.map(([ccy, v]) => (
        <div key={ccy} className="tabular">
          {money(v, ccy)}
        </div>
      ))}
    </div>
  );
}

const columns = [
  col.accessor("device_name", {
    header: "Device",
    cell: (c) => (
      <div>
        <div className="font-semibold">{c.getValue()}</div>
        <div className="text-muted-foreground text-xs">as of {dmy(c.row.original.as_of)}</div>
      </div>
    ),
  }),
  col.accessor("invoices_count", {
    header: "Invoices",
    meta: { numeric: true },
    cell: (c) => (
      <span>
        {int(c.getValue())}
        {c.row.original.invoices_void_count ? (
          <span className="text-muted-foreground text-xs">
            {" "}
            ({c.row.original.invoices_void_count} void)
          </span>
        ) : null}
      </span>
    ),
  }),
  col.accessor("invoiced_totals", {
    header: "Invoiced",
    meta: { numeric: true },
    cell: (c) => <Totals totals={c.getValue()} />,
  }),
  col.accessor("stock_pcs", {
    header: "Stock",
    meta: { numeric: true },
    cell: (c) => (
      <span>
        {int(c.getValue())} pcs
        <span className="text-muted-foreground text-xs"> · {grams(c.row.original.stock_wt)} g</span>
      </span>
    ),
  }),
  col.accessor("customers_count", {
    header: "Customers",
    meta: { numeric: true },
    cell: (c) => int(c.getValue()),
  }),
  col.accessor("last_invoice_at", {
    header: "Last invoice",
    cell: (c) => <span title={c.getValue() ?? undefined}>{relativeTime(c.getValue())}</span>,
  }),
  col.accessor("app_version", { header: "App", cell: (c) => c.getValue() ?? "—" }),
] as ColumnDef<DeviceMetrics, unknown>[];

function MetricsLoaded({ m }: { m: OrgMetrics }) {
  const daily = m.daily.map((d) => ({ ...d, label: d.day.slice(5) }));
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile label="Invoices (30 d)" value={int(m.totals.invoices_30d)} />
        <KpiTile label="Scans (30 d)" value={int(m.totals.scans_30d)} />
        <KpiTile
          label="Crashes (30 d)"
          value={int(m.totals.crashes_30d)}
          className={m.totals.crashes_30d > 0 ? "border-warning/50" : undefined}
        />
        <KpiTile label="Active devices" value={int(m.totals.active_devices)} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Invoices per day</CardTitle>
          </CardHeader>
          <CardContent>
            {daily.length ? (
              <TimeSeries
                title="Invoices per day"
                data={daily}
                xKey="label"
                kind="area"
                series={[
                  { key: "invoices_issued", label: "Issued" },
                  { key: "invoices_voided", label: "Voided" },
                ]}
                valueFormatter={(v) => int(v)}
              />
            ) : (
              <p className="text-muted-foreground text-sm">No daily data yet.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Verify scans per day</CardTitle>
          </CardHeader>
          <CardContent>
            {daily.length ? (
              <TimeSeries
                title="Verify scans per day"
                data={daily}
                xKey="label"
                series={[{ key: "scans", label: "Scans" }]}
                valueFormatter={(v) => int(v)}
              />
            ) : (
              <p className="text-muted-foreground text-sm">No daily data yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
      <div>
        <h2 className="mb-2 font-semibold">Per-device snapshot</h2>
        <DataTable
          columns={columns}
          data={m.devices}
          getRowId={(d) => d.device_id}
          emptyMessage="No device snapshots reported yet."
        />
      </div>
    </div>
  );
}

export function MetricsTab({ orgId }: { orgId: string }) {
  const metrics = useOrgMetrics(orgId);
  return (
    <QueryState query={metrics} rows={6}>
      {(m) => <MetricsLoaded m={m} />}
    </QueryState>
  );
}
